/**
 * The station pages' controller (M6 plan P34) inside the Registration tab: which page shows (the Stations list,
 * the commissioning screen or one station), the views they read (`commissionView`, `playerStationViews`,
 * `captainView(null)`, `corpView`), the drafts, and the commands sent through the `fleet` service's `run` (the
 * wallet filled in there). The Registration panel renders `node()` instead of the Corporation screen while a station
 * page is open. UI events only.
 */
import type { ReactNode } from 'react'
import { logger } from '../../core/logger'
import type { StrategicClient } from '../../strategic/client'
import {
  STATION_TYPE_NAMES,
  type CaptainSummary,
  type CommissionSystemView,
  type CommissionView,
  type CorpFactionId,
  type CorpView,
  type PlayerStationView,
  type TradePermissionName,
} from '../../strategic/types'
import { errorText } from '../fleet/models/outcome'
import { Commission } from './Commission'
import { StationManage } from './StationManage'
import { Stations, type ClaimDraft } from './Stations'
import {
  charterCommand,
  commissionCommand,
  commissionModel,
  cycle,
  dockingCommand,
  managerChoices,
  managerCommand,
  nextDocking,
  nextPermission,
  nextPolicy,
  permissionCommand,
  policyCommand,
  policyDraft,
  stationRefusal,
  stepLimit,
  stepTariff,
  tariffCommand,
  tidyDraft,
  upgradeCommand,
  type CommissionDraft,
  type PolicyDraft,
} from './stationsModel'
import type { RegistrationFleet } from './registration'

export type StationsPage = { kind: 'stations' } | { kind: 'commission' } | { kind: 'manage'; id: string }

export interface StationsHostDeps {
  fleet(): RegistrationFleet | null
  client: Pick<StrategicClient, 'commissionView' | 'playerStationViews' | 'captainView' | 'corpView'>
  systemName(id: string): string
  holderName(holder: CorpFactionId | null): string
  /** The Career's standing with a faction (0 for the player's own or none). */
  standing(holder: CorpFactionId | null): number
  /** Re-renders the panel. */
  render(): void
  /** Back to the Corporation screen. */
  close(): void
}

export interface StationsHost {
  readonly page: StationsPage | null
  open(page: StationsPage): void
  node(): ReactNode
}

export function createStationsHost(deps: StationsHostDeps): StationsHost {
  let page: StationsPage | null = null
  let commission: CommissionView | null = null
  let stations: PlayerStationView[] = []
  let hired: CaptainSummary[] = []
  let claims: CorpView['claims'] = []
  let status: string | null = null
  let busy = false
  let draft: CommissionDraft = { systemId: '', site: null, type: 'outpost', level: 1, autoBuy: true }
  const claimDrafts = new Map<string, ClaimDraft>()
  let policy: { id: string; draft: PolicyDraft } | null = null
  let permission: { id: string; value: TradePermissionName } | null = null

  async function reload(): Promise<void> {
    try {
      const [cv, sv, capt, corp] = await Promise.all([
        deps.client.commissionView(),
        deps.client.playerStationViews(),
        deps.client.captainView(null),
        deps.client.corpView(),
      ])
      commission = cv
      stations = sv
      hired = capt.hired
      claims = corp.claims
      claimDrafts.clear()
      for (const c of corp.claims) {
        const sys = cv.systems.find((s) => s.systemId === c.systemId)
        claimDrafts.set(c.systemId, { tariff: sys?.manualTariff ?? null, docking: c.docking })
      }
      if (draft.systemId === '') draft = { ...draft, systemId: cv.systems.find((s) => s.charters > 0)?.systemId ?? '' }
      draft = tidyDraft(cv, draft)
    } catch (err: unknown) {
      logger.warn('stations: could not read the station views', err)
    }
    deps.render()
  }

  function act(fn: (fleet: RegistrationFleet) => Promise<string>): void {
    const fleet = deps.fleet()
    if (busy) return
    if (fleet === null) {
      status = errorText('noFleet')
      deps.render()
      return
    }
    busy = true
    deps.render()
    void fn(fleet)
      .catch((err: unknown) => refusal(err instanceof Error ? err.message : String(err)))
      .then(async (text) => {
        status = text
        await reload()
      })
      .finally(() => {
        busy = false
        deps.render()
      })
  }

  function refusal(code: string): string {
    return stationRefusal(code) ?? errorText(code)
  }

  function send(cmd: Parameters<RegistrationFleet['run']>[0], done: string): void {
    act(async (fleet) => {
      const r = await fleet.run(cmd)
      return r.ok ? done : refusal(r.error)
    })
  }

  function standingOf(s: CommissionSystemView): number {
    return deps.standing(s.holder)
  }

  function stationsNode(cv: CommissionView): ReactNode {
    return (
      <Stations
        commission={cv}
        stations={stations}
        claims={claims}
        drafts={claimDrafts}
        busy={busy}
        status={status}
        systemName={deps.systemName}
        onBack={() => {
          page = null
          status = null
          deps.close()
        }}
        onCommission={() => host.open({ kind: 'commission' })}
        onManage={(id) => host.open({ kind: 'manage', id })}
        onTariff={(sys, d) => {
          const c = claimDrafts.get(sys)
          if (c === undefined) return
          claimDrafts.set(sys, { ...c, tariff: d === 0 ? null : stepTariff(c.tariff, d) })
          deps.render()
        }}
        onDocking={(sys, d) => {
          const c = claimDrafts.get(sys)
          if (c === undefined) return
          claimDrafts.set(sys, { ...c, docking: nextDocking(c.docking, d) })
          deps.render()
        }}
        onApplyClaim={(sys) => {
          const c = claimDrafts.get(sys)
          if (c === undefined) return
          act(async (fleet) => {
            const t = await fleet.run(tariffCommand(sys, c.tariff))
            if (!t.ok) return refusal(t.error)
            const d = await fleet.run(dockingCommand(sys, c.docking))
            return d.ok ? `${deps.systemName(sys)}: claim settings applied.` : refusal(d.error)
          })
        }}
      />
    )
  }

  function commissionNode(cv: CommissionView): ReactNode {
    return (
      <Commission
        view={cv}
        draft={draft}
        busy={busy}
        status={status}
        systemName={deps.systemName}
        holderName={deps.holderName}
        standing={standingOf}
        onBack={() => host.open({ kind: 'stations' })}
        onBuyCharter={(sys) => {
          const s = cv.systems.find((x) => x.systemId === sys)
          if (s === undefined) return
          draft = { ...draft, systemId: sys }
          send(charterCommand(sys, standingOf(s)), `Charter bought for ${deps.systemName(sys)}.`)
        }}
        onSystem={(sys) => {
          draft = tidyDraft(cv, { ...draft, systemId: sys, site: null })
          deps.render()
        }}
        onSite={(index) => {
          draft = { ...draft, site: index }
          deps.render()
        }}
        onType={(d) => {
          const offered = STATION_TYPE_NAMES.filter((t) => cv.types.some((x) => x.type === t))
          draft = tidyDraft(cv, { ...draft, type: cycle(offered, draft.type, d) })
          deps.render()
        }}
        onLevel={(d) => {
          const levels = cv.types.find((t) => t.type === draft.type)?.levels.map((l) => l.level) ?? [1]
          draft = { ...draft, level: cycle(levels, draft.level, d) }
          deps.render()
        }}
        onAutoBuy={() => {
          draft = { ...draft, autoBuy: !draft.autoBuy }
          deps.render()
        }}
        onCommission={() => {
          const m = commissionModel(cv, draft)
          const site = draft.site
          if (!m.canCommission || site === null) return
          const d = { ...draft, site }
          act(async (fleet) => {
            const r = await fleet.run(commissionCommand(d))
            if (!r.ok) return refusal(r.error)
            if (r.createdId !== undefined) page = { kind: 'manage', id: r.createdId }
            return 'Commissioned. Construction starts as the wares arrive.'
          })
        }}
      />
    )
  }

  function manageNode(id: string): ReactNode {
    const s = stations.find((x) => x.id === id)
    if (s === undefined) return <p className="sui-empty">Reading the station…</p>
    if (policy === null || policy.id !== id) policy = { id, draft: policyDraft(s) }
    if (permission === null || permission.id !== id) permission = { id, value: s.permission }
    const pol = policy
    const perm = permission
    const shipJob = (c: CaptainSummary): string | null =>
      c.shipId !== null ? 'a ship' : c.stationId !== null ? (stations.find((x) => x.id === c.stationId)?.name ?? c.stationId) : null
    return (
      <StationManage
        station={s}
        policy={pol.draft}
        permission={perm.value}
        managers={managerChoices(id, s.manager, hired, shipJob)}
        busy={busy}
        status={status}
        systemName={deps.systemName}
        onBack={() => host.open({ kind: 'stations' })}
        onPolicy={(d) => {
          pol.draft = { ...pol.draft, policy: nextPolicy(pol.draft.policy, d) }
          deps.render()
        }}
        onLimit={(ware, side, d) => {
          pol.draft = stepLimit(pol.draft, ware, side, d)
          deps.render()
        }}
        onSavePolicy={() => {
          policy = null
          send(policyCommand(id, pol.draft), 'Price policy saved.')
        }}
        onPermission={(d) => {
          perm.value = nextPermission(perm.value, d)
          deps.render()
        }}
        onSavePermission={() => {
          permission = null
          send(permissionCommand(id, perm.value), 'Trade permissions saved.')
        }}
        onUpgrade={() => send(upgradeCommand(id), 'Upgrade started. Construction starts as the wares arrive.')}
        onManager={(captainId) => send(managerCommand(id, captainId), captainId === null ? 'The manager is released.' : 'The new manager takes over.')}
      />
    )
  }

  const host: StationsHost = {
    get page(): StationsPage | null {
      return page
    },
    open(next: StationsPage): void {
      page = next
      status = null
      policy = null
      permission = null
      deps.render()
      void reload()
    },
    node(): ReactNode {
      const cv = commission
      if (page === null) return null
      if (cv === null) return <p className="sui-empty">Reading the station registry…</p>
      if (page.kind === 'commission') return commissionNode(cv)
      if (page.kind === 'manage') return manageNode(page.id)
      return stationsNode(cv)
    },
  }
  return host
}
